import { BASE_API_URL } from '@/constant/api'
import { AppResp } from '@/types/api'
import {
  MsgLikeOrFeedbackAddReq,
  MsgFeedbackQueryReq,
  MsgFeedbackStatus,
  MsgFeedbackListQueryReq,
  MsgFeedbackList,
  UserFeedbackAddReq,
  StoryLikeOrFeedbackAddReq,
  StoryFeedbackListQueryReq,
  StoryFeedbackQueryReq,
} from '@/types/feedback'
import { axiosAuth } from '@/utils/axios'
import { uploadFile, UploadScene } from '@/apis/upload'

// 添加聊天反馈 -- 整合点赞点踩
export const addMessageLikeOrFeedback = async (
  req: MsgLikeOrFeedbackAddReq
) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/message/feedback`,
    req
  )

  return res.data.data
}
export const addStoryLikeOrFeedback = async (
  req: StoryLikeOrFeedbackAddReq
) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/message/story/feedback`,
    req
  )

  return res.data.data
}

export const addUserFeedback = async (req: UserFeedbackAddReq) => {
  const formData = new FormData()
  formData.append('type', req.type)
  formData.append('content', req.content)

  if (req.images && req.images.length > 0) {
    const { objectKeys, fallbackFiles } = await uploadImagesWithPresign({
      images: req.images,
      scene: UploadScene.FEEDBACK_IMAGE,
    })
    objectKeys.forEach((key) => formData.append('object_keys', key))
    fallbackFiles.forEach((file) => formData.append('images', file))
  }

  const videoObjectKeys = [...(req.video_object_keys || [])]
  if (req.videos && req.videos.length > 0) {
    const uploadedVideoObjectKeys = await uploadVideosWithPresign({
      videos: req.videos,
      scene: UploadScene.FEEDBACK_VIDEO,
    })
    videoObjectKeys.push(...uploadedVideoObjectKeys)
  }
  videoObjectKeys.forEach((key) => formData.append('video_object_keys', key))

  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/feedback`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )

  return res.data
}

// 获取聊天反馈
export const getMessageFeedback = async (req: MsgFeedbackQueryReq) => {
  const res = await axiosAuth.post<AppResp<MsgFeedbackStatus>>(
    `${BASE_API_URL}/message/feedback_status`,
    req
  )

  return res.data.data
}

// 获取聊天反馈
export const getStoryFeedback = async (req: StoryFeedbackQueryReq) => {
  const res = await axiosAuth.post<AppResp<MsgFeedbackStatus>>(
    `${BASE_API_URL}/message/story/feedback_status`,
    req
  )

  return res.data.data
}
// 获取聊天反馈列表
export const getMessageFeedbackList = async (
  character_id: string,
  startSequence: number | 'max',
  size: number
) => {
  let sequence
  if (startSequence === 'max') {
    sequence = '0'
  } else {
    sequence = String(startSequence)
  }
  const req: MsgFeedbackListQueryReq = { character_id, sequence, size }
  const res = await axiosAuth.post<AppResp<MsgFeedbackList>>(
    `${BASE_API_URL}/message/batch_feedback_status`,
    req
  )

  return res.data.data
}

export const getStoryFeedbackList = async (
  story_id: string,
  startSequence: number | 'max',
  size: number
) => {
  let sequence
  if (startSequence === 'max') {
    sequence = '0'
  } else {
    sequence = String(startSequence)
  }
  const req: StoryFeedbackListQueryReq = { story_id, sequence, size }
  const res = await axiosAuth.post<AppResp<MsgFeedbackList>>(
    `${BASE_API_URL}/message/story/batch_feedback_status`,
    req
  )

  return res.data.data
}

export const addUserReport = async (req: UserFeedbackAddReq) => {
  const formData = new FormData()
  formData.append('type', req.type)
  formData.append('content', req.content)

  if (req.images && req.images.length > 0) {
    const { objectKeys, fallbackFiles } = await uploadImagesWithPresign({
      images: req.images,
      scene: UploadScene.REPORT_IMAGE,
    })
    objectKeys.forEach((key) => formData.append('object_keys', key))
    fallbackFiles.forEach((file) => formData.append('images', file))
  }

  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/user/reports`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )

  return res.data
}

export const addMemoryReport = async (plot_id: string, reason: string) => {
  const res = await axiosAuth.post<AppResp<undefined>>(
    `${BASE_API_URL}/plot/report`,
    {
      plot_id,
      reason,
    }
  )
  return res.data.data
}

const normalizeImageToFile = async (image: File | string, index: number) => {
  return normalizeUploadInputToFile(image, index, {
    fallbackPrefix: 'image',
    fallbackType: 'image/jpeg',
  })
}

const normalizeVideoToFile = async (video: File | string, index: number) => {
  return normalizeUploadInputToFile(video, index, {
    fallbackPrefix: 'video',
    fallbackType: 'video/mp4',
  })
}

const normalizeUploadInputToFile = async (
  input: File | string,
  index: number,
  {
    fallbackPrefix,
    fallbackType,
  }: {
    fallbackPrefix: string
    fallbackType: string
  }
) => {
  if (input instanceof File) {
    return input
  }
  const res = await fetch(input)
  const blob = await res.blob()
  const extension = (blob.type || fallbackType).split('/')[1] || 'bin'
  return new File([blob], `${fallbackPrefix}${index}.${extension}`, {
    type: blob.type || fallbackType,
  })
}

const uploadImagesWithPresign = async ({
  images,
  scene,
}: {
  images: (File | string)[]
  scene: UploadScene
}) => {
  const objectKeys: string[] = []
  const fallbackFiles: File[] = []

  for (let i = 0; i < images.length; i += 1) {
    const image = images[i]
    const file = await normalizeImageToFile(image, i)
    try {
      const uploadRes = await uploadFile({
        file,
        scene,
      })
      objectKeys.push(uploadRes.key)
    } catch (error) {
      console.error(
        'Failed to upload image via presign, fallback to images',
        error
      )
      fallbackFiles.push(file)
    }
  }

  return { objectKeys, fallbackFiles }
}

const uploadVideosWithPresign = async ({
  videos,
  scene,
}: {
  videos: (File | string)[]
  scene: UploadScene
}) => {
  const objectKeys: string[] = []

  for (let i = 0; i < videos.length; i += 1) {
    const video = videos[i]
    const file = await normalizeVideoToFile(video, i)
    const uploadRes = await uploadFile({
      file,
      scene,
    })
    objectKeys.push(uploadRes.key)
  }

  return objectKeys
}
